import { createApiService } from './api';
import type {
  Permission,
  PermissionRequest,
  PermissionUpdateRequest,
  Role,
  RoleRequest,
  RoleUpdateRequest,
  ApiResponse,
  PaginatedResponse,
  ListPermissionsParams,
  ListRolesParams,
  PermissionStatistics,
  RoleStatistics,
  InitializeResponse,
  AddPermissionsRequest,
  IdentityUser,
  UpdateUserRolesRequest,
} from '@/types/permissionsRoles';
import { unwrapPaginatedResponse as unwrapPaginatedResponseUtil, type ApiResponse as WrapperApiResponse } from '@/utils/pagination';

// User Identity Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const USER_IDENTITY_API_BASE_URL =
  import.meta.env.VITE_USER_IDENTITY_API_BASE_URL ||
  import.meta.env.VITE_FIVE_USER_IDENTITY_SERVICE_URL ||
  '/api/user-identity';

const permissionsRolesApiService = createApiService(USER_IDENTITY_API_BASE_URL);

// Helper to unwrap ApiResponse (non-paginated responses)
const unwrapResponse = <T>(response: ApiResponse<T> | T): T => {
  // Handle null or undefined response
  if (!response) {
    throw new Error('API returned empty response');
  }

  // Check if response is a plain object (not array, not Date, etc.)
  const isPlainObject = typeof response === 'object' && response !== null && !Array.isArray(response);

  // If response is already the data type (no wrapper), return as-is
  // This handles Role API which returns the role directly
  if (isPlainObject && !('success' in response)) {
    return response as T;
  }

  // If response has 'success' field, it's wrapped in ApiResponse
  if (isPlainObject && 'success' in response) {
    const wrapped = response as ApiResponse<T> & { timestamp?: number };
    
    // Handle wrapped response
    if (wrapped.success === false) {
      throw new Error(wrapped.message || 'API request failed');
    }
    
    // Validate that data exists when success is true
    if (wrapped.success && (wrapped.data === undefined || wrapped.data === null)) {
      throw new Error(wrapped.message || 'API request returned no data');
    }
    
    // Return the unwrapped data
    return wrapped.data as T;
  }

  // Fallback: if it's not a plain object or doesn't match expected structure, return as-is
  // This handles edge cases like arrays or other types
  return response as T;
};

// Wrapper to use shared utility with ApiResponse type compatibility
const unwrapPaginatedResponse = <T>(response: ApiResponse<PaginatedResponse<T>> | ApiResponse<unknown> | PaginatedResponse<T> | unknown): PaginatedResponse<T> => {
  // Convert ApiResponse format to WrapperApiResponse format for shared utility
  if (response && typeof response === 'object' && response !== null && 'success' in response) {
    const wrapped = response as ApiResponse<unknown>;
    const wrapperResponse: WrapperApiResponse<unknown> = {
      success: wrapped.success,
      data: wrapped.data,
      message: wrapped.message,
      errorCode: wrapped.errorCode,
    };
    return unwrapPaginatedResponseUtil<T>(wrapperResponse);
  }
  return unwrapPaginatedResponseUtil<T>(response);
};

// ============================================================================
// Permission APIs
// ============================================================================

export const permissionApi = {
  // Create permission
  create: async (data: PermissionRequest): Promise<Permission> => {
    const response = await permissionsRolesApiService.post<ApiResponse<Permission>>(
      '/api/v1/permissions',
      data
    );
    return unwrapResponse(response);
  },

  // List permissions with pagination and filters
  list: async (params?: ListPermissionsParams): Promise<PaginatedResponse<Permission>> => {
    try {
      const queryParams = new URLSearchParams();
      // Always include page and size with defaults if not provided
      const page = params?.page !== undefined ? params.page : 0;
      const size = params?.size !== undefined ? params.size : 20;
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      
      if (params?.sort) queryParams.append('sort', params.sort);
      if (params?.active !== undefined) queryParams.append('active', params.active.toString());
      if (params?.systemPermission !== undefined) queryParams.append('systemPermission', params.systemPermission.toString());
      if (params?.resource) queryParams.append('resource', params.resource);
      if (params?.action) queryParams.append('action', params.action);
      if (params?.search) queryParams.append('search', params.search);

      const query = queryParams.toString();
      const endpoint = `/api/v1/permissions?${query}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.list] Calling:', `${USER_IDENTITY_API_BASE_URL}${endpoint}`);
      }
      
      const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Permission>>>(
        endpoint
      );
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.list] Raw response:', response);
      }
      
      const transformed = unwrapPaginatedResponse<Permission>(response);
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.list] Transformed response:', {
          contentLength: transformed.content?.length || 0,
          totalElements: transformed.page?.totalElements || 0,
          totalPages: transformed.page?.totalPages || 0,
        });
      }
      
      return transformed;
    } catch (error) {
      console.error('[permissionApi.list] Error fetching permissions:', error);
      throw error;
    }
  },

  // Get permission by ID
  getById: async (permissionId: string): Promise<Permission> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.getById] Fetching permission:', permissionId);
      }
      const response = await permissionsRolesApiService.get<ApiResponse<Permission>>(
        `/api/v1/permissions/${permissionId}`
      );
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.getById] Response received:', response);
        console.debug('[permissionApi.getById] Response type:', typeof response);
        console.debug('[permissionApi.getById] Response has success:', response && typeof response === 'object' && 'success' in response);
      }
      const unwrapped = unwrapResponse(response);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[permissionApi.getById] Unwrapped permission:', unwrapped);
        console.debug('[permissionApi.getById] Unwrapped permission keys:', unwrapped ? Object.keys(unwrapped) : 'null');
      }
      return unwrapped;
    } catch (error) {
      console.error('[permissionApi.getById] Error fetching permission:', permissionId, error);
      console.error('[permissionApi.getById] Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('[permissionApi.getById] Error message:', error instanceof Error ? error.message : String(error));
      if (error instanceof Error && error.stack) {
        console.error('[permissionApi.getById] Error stack:', error.stack);
      }
      throw error;
    }
  },

  // Update permission
  // Note: name cannot be updated - only description, resource, action, and active can be changed
  update: async (permissionId: string, data: PermissionUpdateRequest): Promise<Permission> => {
    // Build update payload excluding name (name cannot be changed)
    const updatePayload = {
      description: data.description,
      resource: data.resource,
      action: data.action,
      active: data.active,
    };
    
    // Remove undefined fields
    Object.keys(updatePayload).forEach(key => {
      if (updatePayload[key as keyof typeof updatePayload] === undefined) {
        delete updatePayload[key as keyof typeof updatePayload];
      }
    });
    
    const response = await permissionsRolesApiService.put<ApiResponse<Permission>>(
      `/api/v1/permissions/${permissionId}`,
      updatePayload
    );
    return unwrapResponse(response);
  },

  // Delete permission
  delete: async (permissionId: string): Promise<void> => {
    const response = await permissionsRolesApiService.delete<ApiResponse<void>>(
      `/api/v1/permissions/${permissionId}`
    );
    unwrapResponse(response);
  },

  // Toggle permission status
  toggleStatus: async (permissionId: string, active: boolean): Promise<Permission> => {
    const response = await permissionsRolesApiService.patch<ApiResponse<Permission>>(
      `/api/v1/permissions/${permissionId}/status?active=${active}`,
      {}
    );
    return unwrapResponse(response);
  },

  // Get active permissions
  getActive: async (params?: Omit<ListPermissionsParams, 'active'>): Promise<PaginatedResponse<Permission>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Permission>>>(
      `/api/v1/permissions/active${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get system permissions
  getSystem: async (params?: Omit<ListPermissionsParams, 'systemPermission'>): Promise<PaginatedResponse<Permission>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Permission>>>(
      `/api/v1/permissions/system${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get permissions by resource
  getByResource: async (resource: string, params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<Permission>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Permission>>>(
      `/api/v1/permissions/resource/${resource}${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get permissions by action
  getByAction: async (action: string, params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<Permission>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Permission>>>(
      `/api/v1/permissions/action/${action}${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get permission statistics
  getStatistics: async (): Promise<PermissionStatistics> => {
    const response = await permissionsRolesApiService.get<ApiResponse<{
      totalPermissions?: number;
      activePermissions?: number;
      inactivePermissions?: number;
      systemPermissions?: number;
      customPermissions?: number;
      byResource?: Record<string, number>;
      byAction?: Record<string, number>;
      recentActivity?: Permission[];
    }> | {
      totalPermissions?: number;
      activePermissions?: number;
      inactivePermissions?: number;
      systemPermissions?: number;
      customPermissions?: number;
      byResource?: Record<string, number>;
      byAction?: Record<string, number>;
      recentActivity?: Permission[];
    }>('/api/v1/permissions/statistics');
    
    // Unwrap response (could be ApiResponse wrapped or direct object)
    const unwrapped = unwrapResponse<{
      totalPermissions?: number;
      activePermissions?: number;
      inactivePermissions?: number;
      systemPermissions?: number;
      customPermissions?: number;
      byResource?: Record<string, number>;
      byAction?: Record<string, number>;
      recentActivity?: Permission[];
    }>(response);
    
    // Transform API response format to frontend format
    const stats: PermissionStatistics = {
      total: unwrapped.totalPermissions ?? 0,
      active: unwrapped.activePermissions ?? 0,
      inactive: unwrapped.inactivePermissions ?? (unwrapped.totalPermissions ?? 0) - (unwrapped.activePermissions ?? 0),
      system: unwrapped.systemPermissions ?? 0,
      custom: unwrapped.customPermissions ?? 0,
      byResource: unwrapped.byResource ?? {},
      byAction: unwrapped.byAction ?? {},
      recentActivity: unwrapped.recentActivity ?? [],
    };
    
    return stats;
  },

  // Initialize permissions
  initialize: async (): Promise<InitializeResponse> => {
    const response = await permissionsRolesApiService.post<ApiResponse<InitializeResponse>>(
      '/api/v1/permissions/initialize',
      {}
    );
    return unwrapResponse(response);
  },
};

// ============================================================================
// Role APIs
// ============================================================================

export const roleApi = {
  // Create role
  create: async (data: RoleRequest): Promise<Role> => {
    const response = await permissionsRolesApiService.post<ApiResponse<Role>>(
      '/api/v1/roles',
      data
    );
    return unwrapResponse(response);
  },

  // List roles with pagination and filters
  list: async (params?: ListRolesParams): Promise<PaginatedResponse<Role>> => {
    try {
      const queryParams = new URLSearchParams();
      // Always include page and size with defaults if not provided
      const page = params?.page !== undefined ? params.page : 0;
      const size = params?.size !== undefined ? params.size : 20;
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());
      
      if (params?.sort) queryParams.append('sort', params.sort);
      if (params?.active !== undefined) queryParams.append('active', params.active.toString());
      if (params?.systemRole !== undefined) queryParams.append('systemRole', params.systemRole.toString());
      if (params?.minPermissionCount !== undefined) queryParams.append('minPermissionCount', params.minPermissionCount.toString());
      if (params?.maxPermissionCount !== undefined) queryParams.append('maxPermissionCount', params.maxPermissionCount.toString());
      if (params?.search) queryParams.append('search', params.search);

      const query = queryParams.toString();
      const endpoint = `/api/v1/roles?${query}`;
      
      console.log('[roleApi.list] Calling:', `${USER_IDENTITY_API_BASE_URL}${endpoint}`);
      
      // Roles API returns Spring Data format directly (no ApiResponse wrapper)
      const response = await permissionsRolesApiService.get<PaginatedResponse<Role> | ApiResponse<PaginatedResponse<Role>>>(
        endpoint
      );
      
      console.log('[roleApi.list] Raw response:', response);
      
      const transformed = unwrapPaginatedResponse<Role>(response);
      
      console.log('[roleApi.list] Transformed response:', {
        contentLength: transformed.content?.length || 0,
        totalElements: transformed.page?.totalElements || 0,
        totalPages: transformed.page?.totalPages || 0,
      });
      
      return transformed;
    } catch (error) {
      console.error('[roleApi.list] Error fetching roles:', error);
      throw error;
    }
  },

  // Get role by ID
  // Note: Role API returns the role object directly (unwrapped), not in ApiResponse wrapper
  getById: async (roleId: string): Promise<Role> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[roleApi.getById] Fetching role:', roleId);
      }
      // Role API returns the role directly, not wrapped in ApiResponse
      const response = await permissionsRolesApiService.get<ApiResponse<Role> | Role>(
        `/api/v1/roles/${roleId}`
      );
      if (process.env.NODE_ENV === 'development') {
        console.debug('[roleApi.getById] Response received:', response);
        console.debug('[roleApi.getById] Response type:', typeof response);
        console.debug('[roleApi.getById] Response has success:', response && typeof response === 'object' && 'success' in response);
      }
      const unwrapped = unwrapResponse(response);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[roleApi.getById] Unwrapped role:', unwrapped);
      }
      return unwrapped;
    } catch (error) {
      console.error('[roleApi.getById] Error fetching role:', roleId, error);
      throw error;
    }
  },

  // Update role
  // Note: name cannot be updated - only description, permissionIds, and active can be changed
  update: async (roleId: string, data: RoleUpdateRequest): Promise<Role> => {
    // Build update payload excluding name (name cannot be changed, especially for system roles)
    const updatePayload: RoleUpdateRequest = {
      description: data.description,
      permissionIds: data.permissionIds,
      active: data.active,
    };
    
    // Remove undefined fields
    Object.keys(updatePayload).forEach(key => {
      if (updatePayload[key as keyof RoleUpdateRequest] === undefined) {
        delete updatePayload[key as keyof RoleUpdateRequest];
      }
    });
    
    const response = await permissionsRolesApiService.put<ApiResponse<Role>>(
      `/api/v1/roles/${roleId}`,
      updatePayload
    );
    return unwrapResponse(response);
  },

  // Delete role
  delete: async (roleId: string): Promise<void> => {
    const response = await permissionsRolesApiService.delete<ApiResponse<void>>(
      `/api/v1/roles/${roleId}`
    );
    unwrapResponse(response);
  },

  // Toggle role status
  toggleStatus: async (roleId: string, active: boolean): Promise<Role> => {
    const response = await permissionsRolesApiService.patch<ApiResponse<Role>>(
      `/api/v1/roles/${roleId}/status?active=${active}`,
      {}
    );
    return unwrapResponse(response);
  },

  // Get active roles
  getActive: async (params?: Omit<ListRolesParams, 'active'>): Promise<PaginatedResponse<Role>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Role>>>(
      `/api/v1/roles/active${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get system roles
  getSystem: async (params?: Omit<ListRolesParams, 'systemRole'>): Promise<PaginatedResponse<Role>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await permissionsRolesApiService.get<ApiResponse<PaginatedResponse<Role>>>(
      `/api/v1/roles/system${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse(response);
  },

  // Get roles by permission
  // Note: If API doesn't provide this endpoint, we filter client-side
  getByPermission: async (permissionName: string, params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<Role>> => {
    try {
      // Try to fetch all roles and filter client-side
      // This is less efficient but works if the API doesn't provide a dedicated endpoint
      const allRoles = await roleApi.list({ 
        page: params?.page || 0, 
        size: params?.size || 1000, // Get enough to filter
        sort: params?.sort 
      });
      
      // Filter roles that have the specified permission
      const filteredRoles = allRoles.content.filter(role => 
        role.permissions?.some(perm => perm.name === permissionName)
      );
      
      return {
        content: filteredRoles,
        page: {
          size: filteredRoles.length,
          number: params?.page || 0,
          totalElements: filteredRoles.length,
          totalPages: 1,
        },
      };
    } catch (error) {
      console.error('[roleApi.getByPermission] Error fetching roles by permission:', error);
      // If filtering fails, return empty result
      return {
        content: [],
        page: {
          size: 0,
          number: params?.page || 0,
          totalElements: 0,
          totalPages: 0,
        },
      };
    }
  },

  // Add permission to role
  addPermission: async (roleId: string, permissionName: string): Promise<Role> => {
    const response = await permissionsRolesApiService.post<ApiResponse<Role>>(
      `/api/v1/roles/${roleId}/permissions/${permissionName}`,
      {}
    );
    return unwrapResponse(response);
  },

  // Remove permission from role
  removePermission: async (roleId: string, permissionName: string): Promise<Role> => {
    const response = await permissionsRolesApiService.delete<ApiResponse<Role>>(
      `/api/v1/roles/${roleId}/permissions/${permissionName}`
    );
    return unwrapResponse(response);
  },

  // Add multiple permissions to role
  addPermissions: async (roleId: string, data: AddPermissionsRequest): Promise<Role> => {
    const response = await permissionsRolesApiService.patch<ApiResponse<Role>>(
      `/api/v1/roles/${roleId}/permissions`,
      data
    );
    return unwrapResponse(response);
  },

  // Get role statistics
  getStatistics: async (): Promise<RoleStatistics> => {
    // Role statistics API returns unwrapped response (no ApiResponse wrapper)
    const response = await permissionsRolesApiService.get<{
      totalRoles?: number;
      activeRoles?: number;
      inactiveRoles?: number;
      systemRoles?: number;
      customRoles?: number;
      permissionDistribution?: Record<number, number>;
      topRolesByPermissionCount?: Array<{
        roleId: string;
        roleName: string;
        permissionCount: number;
      }>;
      recentActivity?: Role[];
    }>('/api/v1/roles/statistics');
    
    // Transform API response format to frontend format
    const stats: RoleStatistics = {
      total: response.totalRoles ?? 0,
      active: response.activeRoles ?? 0,
      inactive: response.inactiveRoles ?? ((response.totalRoles ?? 0) - (response.activeRoles ?? 0)),
      system: response.systemRoles ?? 0,
      custom: response.customRoles ?? 0,
      permissionDistribution: response.permissionDistribution ?? {},
      topRolesByPermissionCount: response.topRolesByPermissionCount ?? [],
      recentActivity: response.recentActivity ?? [],
    };
    
    return stats;
  },

  // Initialize roles
  initialize: async (): Promise<InitializeResponse> => {
    const response = await permissionsRolesApiService.post<ApiResponse<InitializeResponse>>(
      '/api/v1/roles/initialize',
      {}
    );
    return unwrapResponse(response);
  },
};

// ============================================================================
// User Identity APIs (admin: get user by email/id, update user roles — super admin only)
// ============================================================================

// Normalize identity user so roles is always string[] (API may return role names or role objects)
function normalizeIdentityUser(raw: IdentityUser & { roles?: string[] | Array<{ name?: string; id?: string }> }): IdentityUser {
  const roles = raw.roles;
  const roleNames: string[] = Array.isArray(roles)
    ? roles.map((r) => (typeof r === 'string' ? r : (r as { name?: string }).name ?? '')).filter(Boolean)
    : [];
  return { ...raw, roles: roleNames };
}

export const userIdentityApi = {
  /** Get identity user by email (for profile admin page). */
  getByEmail: async (email: string): Promise<IdentityUser | null> => {
    try {
      const response = await permissionsRolesApiService.get<ApiResponse<IdentityUser> | IdentityUser>(
        `/api/v1/users/by-email/${encodeURIComponent(email)}`
      );
      const unwrapped = unwrapResponse(response) as IdentityUser & { roles?: string[] | Array<{ name?: string }> };
      if (!unwrapped) return null;
      return normalizeIdentityUser(unwrapped);
    } catch (err: unknown) {
      const apiErr = err as { status?: number };
      if (apiErr?.status === 404) return null;
      throw err;
    }
  },

  /** Get identity user by ID. */
  getById: async (userId: string): Promise<IdentityUser> => {
    const response = await permissionsRolesApiService.get<ApiResponse<IdentityUser> | IdentityUser>(
      `/api/v1/users/${encodeURIComponent(userId)}`
    );
    const unwrapped = unwrapResponse(response) as IdentityUser & { roles?: string[] | Array<{ name?: string }> };
    return normalizeIdentityUser(unwrapped);
  },

  /** Update a user's roles (super admin only). Replaces all roles with roleNames. */
  updateRoles: async (userId: string, payload: UpdateUserRolesRequest): Promise<IdentityUser> => {
    const response = await permissionsRolesApiService.put<ApiResponse<IdentityUser>>(
      `/api/v1/users/${encodeURIComponent(userId)}/roles`,
      payload
    );
    const unwrapped = unwrapResponse(response) as IdentityUser & { roles?: string[] | Array<{ name?: string }> };
    return normalizeIdentityUser(unwrapped);
  },
};

export { permissionsRolesApiService };
